import { useCluster } from '@cast/shared-cluster-context-data-access';
import { PRODUCT_BASE_PATHS } from '@cast/console-constants';

import {
  PV_POLICY_DUPLICATE_FROM_NAME_URL_KEY,
  PV_POLICY_PAGE_TAB_URL_KEY,
  PV_POLICY_SETTINGS_TAB_URL_KEY,
} from '../pv-policy-details/_constants';
import {
  PvPolicyPageTabId,
  PvPolicySettingsTabId,
} from '../pv-policy-details/_tabs/types';
import { PERSISTENT_VOLUMES_SEARCH_URL_KEY } from '../persistent-volumes/table/_constants';
import { PV_EVENTS_QUERY_PARAM_KEYS } from 'features/cluster/storage-optimization/pv-events/_hooks/usePvEventsTimeRangeSelection';
import { statusCriterion } from 'features/cluster/storage-optimization/pv-events/searchCriteria';
import {
  PersistentVolumeManagementStatusFilter,
  PersistentVolumeStatusFilter,
} from '@cast/types-stop';

export const PERSISTENT_VOLUMES_TAB_QUERY_KEY = 'tab';

type BuildStorageAutoscalerLinksArgs = {
  clusterId: string;
};

export const buildStorageAutoscalerLinks = ({
  clusterId,
}: BuildStorageAutoscalerLinksArgs) => {
  const base = `${PRODUCT_BASE_PATHS.automation}/external-clusters/${clusterId}/storage-autoscaler`;

  return {
    persistentVolumes: (opts?: {
      policyNames?: string[];
      volumeTypes?: string[];
      tab?: 'list' | 'dashboard';
      statuses?: PersistentVolumeStatusFilter[];
      managementStatuses?: PersistentVolumeManagementStatusFilter[];
    }) => {
      const filter: Record<string, unknown> = {};

      if (opts?.policyNames?.length) {
        filter.pvPolicies = opts.policyNames;
      }

      if (opts?.volumeTypes?.length) {
        filter.volumeTypes = opts.volumeTypes;
      }

      if (opts?.statuses?.length) {
        filter.statuses = opts.statuses;
      }

      if (opts?.managementStatuses?.length) {
        filter.managementStatuses = opts.managementStatuses;
      }

      const search = new URLSearchParams();

      if (opts?.tab && opts.tab !== 'dashboard') {
        search.set(PERSISTENT_VOLUMES_TAB_QUERY_KEY, opts.tab);
      }

      if (Object.keys(filter).length) {
        search.set(
          PERSISTENT_VOLUMES_SEARCH_URL_KEY,
          encodeURIComponent(JSON.stringify(filter))
        );
      }

      const qs = search.toString();
      return qs
        ? `${base}/persistent-volumes?${qs}`
        : `${base}/persistent-volumes`;
    },
    persistentVolumeDetails: (pvName: string | undefined) => {
      if (!pvName) {
        throw new Error('persistentVolumeDetails requires pvName');
      }

      return `${base}/persistent-volumes/${pvName}`;
    },
    configuration: () => `${base}/configuration`,
    policies: () => `${base}/policies`,
    createPvPolicy: (opts?: { duplicateFrom?: string }) => {
      const search = new URLSearchParams();

      if (opts?.duplicateFrom) {
        search.set(PV_POLICY_DUPLICATE_FROM_NAME_URL_KEY, opts.duplicateFrom);
      }

      const qs = search.toString();

      return qs ? `${base}/policies/new?${qs}` : `${base}/policies/new`;
    },
    pvPolicyDetails: (
      policyName: string | undefined,
      opts?: { tab?: PvPolicyPageTabId; setting?: PvPolicySettingsTabId }
    ) => {
      if (!policyName) {
        throw new Error('pvPolicyDetails requires policyName');
      }

      const queryParams = new URLSearchParams();

      if (opts?.tab) {
        queryParams.set(PV_POLICY_PAGE_TAB_URL_KEY, opts.tab);
      }

      if (opts?.setting) {
        queryParams.set(PV_POLICY_SETTINGS_TAB_URL_KEY, opts.setting);
      }

      const qs = queryParams.toString();
      const url = `${base}/policies/${policyName}`;

      return qs ? `${url}?${qs}` : url;
    },
    pvEvents: (opts?: {
      status?: 'failure' | 'success';
      timeRange?: {
        fromDate: string;
        toDate: string;
        timeZone?: string;
      };
    }) => {
      const search = new URLSearchParams();
      if (opts?.status) {
        search.set(statusCriterion.key, opts.status);
      }

      if (opts?.timeRange) {
        search.set(
          PV_EVENTS_QUERY_PARAM_KEYS.fromDate,
          opts.timeRange.fromDate
        );
        search.set(PV_EVENTS_QUERY_PARAM_KEYS.toDate, opts.timeRange.toDate);

        if (opts.timeRange.timeZone) {
          search.set(
            PV_EVENTS_QUERY_PARAM_KEYS.timeZone,
            opts.timeRange.timeZone
          );
        }
      }
      const qs = search.toString();
      return qs ? `${base}/events?${qs}` : `${base}/events`;
    },
  };
};

export const useStorageAutoscalerLinks = () => {
  const { cluster } = useCluster();
  return buildStorageAutoscalerLinks({ clusterId: cluster.id });
};
